import { React, useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import "./personal-chart-style.css";
import Menulist from "../../../constant/menulist";
import AnaTabs from "../../custom/anatabs/anatabs";
import BaziChartTable from "../../custom/bazi-chart/bazi-table";
import ZiweiChartTable from "../../custom/ziwei-chart/ziwei-table";
import Loading from "../../dialog/loading/loading";
import GoogleLoginDialog from "../../dialog/google-login/GoogleLoginDialog";
import ConfirmDialog from "../../dialog/confirm/confirm-dialog";
import UserService from "../../../service/user-service";
import AuthService from "../../../service/auth-service";
import PaymentService from "../../../service/payment-service";
import SiteFooter from "../../custom/site-footer/site-footer";
import PageSeo from "../../custom/page-seo/page-seo";
import useIsMobile from "../../../hooks/useIsMobile";
import { trackPurchase } from "../../../utils/meta-pixel";

const TABS_MAP = Menulist.anaTabsMap;

const GAN_ELEMENT = {
  甲: "木",
  乙: "木",
  丙: "火",
  丁: "火",
  戊: "土",
  己: "土",
  庚: "金",
  辛: "金",
  壬: "水",
  癸: "水",
};

//tab 中文 → aspect schema key
const TAB_KEY_MAP = {
  個性: "personality",
  事業: "career",
  財運: "wealth",
  感情: "love",
  健康: "health",
  家庭: "family",
  人際: "social",
};

const PersonalChartPageComponent = ({
  setShowServiceMenu,
  setShowPersonalMenu,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  //完成註冊後要回到本頁
  const registerFrom = location.pathname;

  const isMobile = useIsMobile();
  const [currentTab, setCurrentTab] = useState("個性");
  const [tableIndex, setTableIndex] = useState(0);
  const [chartData, setChartData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true); //命盤資料載入中（很快，<1s）
  const [isAnalyzing, setIsAnalyzing] = useState(false); //面向分析載入中（Claude 30–90s）
  const [progressPercent, setProgressPercent] = useState(0); //分析進度（時間估算 0–95%）

  //—— 紫微 B-3-2 專屬 state（大限 / 流年 picker 切換用） ——
  //  ziweiData：B-3-2 完整回應（natalPlate + decade + flow + combined + daYunMatrix）
  //  selectedDecadeIndex / selectedYear：由 B-3-2 回應同步
  const [ziweiData, setZiweiData] = useState(null);
  const [selectedDecadeIndex, setSelectedDecadeIndex] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  //問問老師 button 只在滑到「七大面向」區塊時顯示（不擋命盤）
  const [askVisible, setAskVisible] = useState(false);
  useEffect(() => {
    let observer = null;
    let retryTimer = null;
    const tryObserve = () => {
      const target = document.getElementById("seven-aspects-section");
      if (!target) return false;
      observer = new IntersectionObserver(
        ([entry]) => setAskVisible(entry.isIntersecting),
        { threshold: 0.05 },
      );
      observer.observe(target);
      return true;
    };
    if (!tryObserve()) {
      retryTimer = setTimeout(tryObserve, 500);
    }
    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      if (observer) observer.disconnect();
    };
  }, [isLoading]);

  //—— Auth gating（雙層）——
  //  此頁要看命盤必須 validCode === "2"；否則：未登入 → login dialog；validCode=1 → register prompt
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);

  const fetchedRef = useRef(false);

  //完整資料 fetch：B-3 拿命盤 + B-5 拿面向分析（依解鎖狀態決定 [0] or [0-6]）
  //  兩階段：
  //    Phase 1 (isLoading)：拿命盤（快、阻塞畫面）→ 收掉全頁 loading
  //    Phase 2 (isAnalyzing)：拿面向分析（Claude 30–90s，背景跑）→ 分析區塊獨立顯示「分析中」遮罩
  //  好處：使用者可立刻看到命盤資訊（姓名、八字、紫微），不用對著整頁 loading 乾等
  const fetchAllData = useCallback(async () => {
    setIsLoading(true);
    const MIN_LOADING_MS = 1000;
    const start = Date.now();

    let chart = null;
    try {
      const chartRes = await UserService.getPersonalChart();
      if (!chartRes.data?.status) return;
      chart = chartRes.data.data;
      setChartData(chart);
    } catch (e) {
      console.log("[PersonalChart] 命盤載入失敗:", e);
    } finally {
      //Phase 1 完成：保證 loading 至少 1 秒避免閃一下
      const remaining = MIN_LOADING_MS - (Date.now() - start);
      if (remaining > 0) {
        await new Promise((r) => setTimeout(r, remaining));
      }
      setIsLoading(false);
    }

    //命盤拿失敗就不用跑 Phase 2
    if (!chart) return;

    //Phase 2：拆「個性快取」+「其他面向 Claude」兩階段，避免 個性 被連帶遮住
    //  原本一次抓 [0..6] 會等所有 Claude 跑完才回，期間 個性 tab 也只能等
    //  改成：先單獨抓 [0]（多半 cache hit、極快）→ 把 個性塞進 analyticsData →
    //        個性 tab 立刻可顯示，overlay 自動消失；
    //        再抓 [1..6]（已解鎖才抓，Claude × 6 慢，期間其他 tab 才顯示 overlay）
    //  ⚠ B-5 現在永遠回全部 7 key（沒 request 的為 null），合併時要跳過 null，
    //     否則 Phase 2b 的 personality=null 會覆蓋掉 Phase 2a 拿到的個性資料
    const mergeNonNull = (prev, next) => {
      const merged = { ...(prev || {}) };
      for (const [k, v] of Object.entries(next || {})) {
        if (v != null) merged[k] = v;
      }
      return merged;
    };

    setIsAnalyzing(true);
    try {
      //2a：個性（cache hit 1 秒內回）
      const r1 = await UserService.getAspectsAnalytics([0]);
      if (r1.data?.status) {
        setAnalyticsData((prev) => mergeNonNull(prev, r1.data.data));
      }

      //2b：其他六個面向（只有解鎖才抓）
      if (chart.hasUnlockedAspects) {
        const r2 = await UserService.getAspectsAnalytics([1, 2, 3, 4, 5, 6]);
        if (r2.data?.status) {
          setAnalyticsData((prev) => mergeNonNull(prev, r2.data.data));
        }
      }
    } catch (e) {
      console.log("[PersonalChart] 面向分析失敗:", e);
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  //B-3-2 紫微資料 fetch（切大限 / 切流年、初次載入共用）
  const fetchZiwei = useCallback(async ({ decadeIndex, year } = {}) => {
    try {
      const res = await UserService.getZiweiPersonalChart({ decadeIndex, year });
      if (res.data?.status) {
        setZiweiData(res.data.data);
        setSelectedDecadeIndex(res.data.data.decade?.index ?? null);
        setSelectedYear(res.data.data.flow?.year ?? null);
      }
    } catch (e) {
      console.log("[PersonalChart] ziwei B-3-2 載入失敗:", e);
    }
  }, []);

  //命盤（B-3）拿到後、順便拉一次 B-3-2 塞初始資料
  useEffect(() => {
    if (chartData && !ziweiData) {
      fetchZiwei();
    }
  }, [chartData, ziweiData, fetchZiwei]);

  //初次載入：先 auth gating，通過才打 B-3 + B-5
  //  未登入 → login dialog（之後 handleLoginSuccess 接手）
  //  已登入但 validCode != 2 → register prompt
  //  validCode === "2" → fetch 命盤資料
  useEffect(() => {
    if (fetchedRef.current) return;
    const isLogin = AuthService.getLoginStatus();
    const profile = AuthService.getUserProfile();
    if (!isLogin || !profile) {
      setIsLoading(false);
      setShowLoginDialog(true);
      return;
    }
    if (profile.userValidCode !== "2") {
      setIsLoading(false);
      setShowRegisterPrompt(true);
      return;
    }
    fetchedRef.current = true;
    fetchAllData();
  }, [fetchAllData]);

  //登入 dialog 成功：依新 validCode 決定下一步
  const handleLoginSuccess = async ({ idToken } = {}) => {
    if (!idToken) return;
    try {
      const res = await AuthService.firebaseLogin(idToken);
      if (res.data.status) {
        AuthService.login(res.data.data);
        setShowLoginDialog(false);
        if (res.data.data.userValidCode !== "2") {
          //剛登入但未完成註冊 → 帶到 /register，註冊完會 navigate 回本頁
          navigate("/register", { state: { from: registerFrom } });
        } else {
          //已是完整使用者 → 直接 fetch
          fetchedRef.current = true;
          fetchAllData();
        }
      }
    } catch (e) {
      console.error("[PersonalChart] firebase-login 失敗:", e);
    }
  };

  //tab 從付款分頁切回來時：直接重打 B-3 + B-5 拿最新狀態
  //  已解鎖（hasUnlockedAspects=true）就跳過，避免無謂重打
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) return;
      if (!chartData || chartData.hasUnlockedAspects) return; //未載入或已解鎖就不查
      fetchAllData();
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [chartData, fetchAllData]);

  //Meta Pixel Purchase 偵測
  //  /checkout 導回時 location.state.pendingOrderID 會帶訂單號
  //  tab 切回 → 打後端 checkOrderPaid 看是否已完成付款 → paid=true 就 fire Purchase + 清 state
  useEffect(() => {
    const pendingOrderID = location.state?.pendingOrderID;
    if (!pendingOrderID) return;

    let cancelled = false;
    const checkAndFire = async () => {
      if (document.hidden || cancelled) return;
      try {
        const res = await PaymentService.checkOrderPaid(pendingOrderID);
        if (cancelled) return;
        const d = res.data?.data;
        if (d?.paid) {
          trackPurchase({
            productID: d.planID || "digital_chart",
            productName: "個人命盤七大面向解析",
            amount: d.amount || 99,
          });
          //清 location.state 避免重複觸發
          navigate(location.pathname, { replace: true, state: {} });
        }
      } catch (e) {
        //靜默失敗（追蹤事件不擋主流程）
      }
    };

    checkAndFire(); //掛載立即跑一次
    document.addEventListener("visibilitychange", checkAndFire);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", checkAndFire);
    };
  }, [location.state, location.pathname, navigate]);

  //分析進度條：Claude API 不回真實進度，這裡用「已耗時 → 估算百分比」的指數曲線
  //  快慢直覺：t=15s≈30%、t=30s≈50%、t=60s≈73%、t=90s≈85%、t→∞ 漸近 95%（封頂）
  //  公式 95 * (1 - exp(-t/40))：保證單調遞增、永不超過 95%（真實回應後 overlay 直接消失）
  useEffect(() => {
    if (!isAnalyzing) {
      setProgressPercent(0);
      return;
    }
    const start = Date.now();
    const tick = () => {
      const elapsedSec = (Date.now() - start) / 1000;
      const pct = 95 * (1 - Math.exp(-elapsedSec / 40));
      setProgressPercent(Math.min(95, Math.floor(pct)));
    };
    tick(); //立刻先跑一次，畫面不會卡 0
    const intervalId = setInterval(tick, 400);
    return () => clearInterval(intervalId);
  }, [isAnalyzing]);

  //十進位度數轉 度°分'方位 格式
  //  經緯度任一無資料（null/undefined/空字串/非數字）→ 回空字串，只隱藏座標、保留「地點」格式
  const formatCoord = (lng, lat) => {
    const parse = (v) =>
      v === null || v === undefined || v === "" || !Number.isFinite(Number(v))
        ? NaN
        : Number(v);
    const nLng = parse(lng);
    const nLat = parse(lat);
    if (Number.isNaN(nLng) || Number.isNaN(nLat)) return "";

    const toDegMin = (val, pos, neg) => {
      const abs = Math.abs(val);
      const deg = Math.floor(abs);
      const min = Math.round((abs - deg) * 60);
      return `${deg}°${min}'${val >= 0 ? pos : neg}`;
    };
    return `${toDegMin(nLng, "E", "W")}, ${toDegMin(nLat, "N", "S")}`;
  };

  //出生地座標字串（無資料時為空字串 → 整列「地點」隱藏）
  const birthCoord = chartData
    ? formatCoord(chartData.userBirthLongitude, chartData.userBirthLatitude)
    : "";

  //當前 tab 對應的面向分析資料
  //  未解鎖時：所有 tab 都填個性內容（讓模糊蓋層後面有東西顯示）
  //  已解鎖時：依 tab 顯示對應面向資料
  const currentAspect = chartData?.hasUnlockedAspects
    ? analyticsData?.[TAB_KEY_MAP[currentTab]]
    : analyticsData?.personality;

  //切換八字與紫微命盤
  const selectTableIndex = (clickIndex) => {
    if (clickIndex == tableIndex) {
      return;
    }
    setTableIndex(tableIndex == 1 ? 0 : 1);
  };

  //按「解鎖」→ 導 /checkout 選發票 → /checkout 建單、跳綠界、導回本頁
  //  付款完成後：本頁的 visibilitychange useEffect 會 refetch，看到 hasUnlockedAspects=true 就重新載入面向分析
  const handlePurchase = () => {
    navigate("/checkout", {
      state: {
        planID: "digital_chart",
        itemName: "個人命盤七大面向解析",
        amount: 99,
        payload: {},
        returnPath: location.pathname,
      },
    });
  };

  return (
    <div
      className="personal-chart-main"
      onMouseEnter={() => {
        setShowServiceMenu(false);
        setShowPersonalMenu(false);
      }}
    >
      <PageSeo
        title="個人專屬命盤｜八字七大面向解析"
        description="獲取你的八字命盤，先看「個性」面向。解鎖後可讀完整七大面向：個性、事業、財運、感情、健康、家庭、人際，由資深命理師團隊深度解析。"
        path="/personal-chart"
      />
      {/* API 載入中（至少顯示 1 秒）*/}
      {isLoading && <Loading />}

      {/* 未登入訪客直接開本頁網址時跳出；關掉 → 留在當前頁，不跳轉 */}
      {showLoginDialog && (
        <GoogleLoginDialog
          open={showLoginDialog}
          onClose={() => {
            setShowLoginDialog(false);
          }}
          onSuccess={handleLoginSuccess}
        />
      )}

      {/* 已登入但未完成註冊；關掉 → 留在當前頁，不跳轉 */}
      <ConfirmDialog
        open={showRegisterPrompt}
        onClose={() => {
          setShowRegisterPrompt(false);
        }}
        onSuccess={() => {
          setShowRegisterPrompt(false);
          navigate("/register", { state: { from: registerFrom } });
        }}
        title="尚未完成註冊"
        message="個人專屬命盤需要完成註冊才能查看，<br>是否前往註冊頁完成？"
        confirmTxt="前往註冊"
      />

      {/* 電腦板佈局 */}
      {!isMobile && (
        <div className="web-main">
          <div className="main">
            <div className="content">
              <div className="left-block">
                <div className="personal-info">
                  <div className="left-info">
                    <p className="name">{chartData?.userName}</p>
                    <div className="space"></div>
                    <p
                      style={birthCoord ? undefined : { visibility: "hidden" }}
                    >
                      地點 {birthCoord}
                    </p>
                  </div>
                  <div className="right-info">
                    <div className="inner">
                      <p>性別 {chartData?.userGender == "1" ? "男" : "女"}</p>
                      <p>
                        西元 {chartData?.baziPlate?.solarBirth?.split(" ")[0]}
                      </p>
                      <p>
                        時間 {chartData?.baziPlate?.solarBirth?.split(" ")[1]} (
                        {chartData?.baziPlate?.solarBirth?.split(" ")[2]})
                      </p>
                    </div>
                  </div>
                </div>

                <div className="chart-plate">
                  <div className="chart-header">
                    <div className="chart-select">
                      <button disabled="false">
                        【命主】{chartData?.baziPlate?.dayMaster}
                        {GAN_ELEMENT[chartData?.baziPlate?.dayMaster] ?? ""}
                      </button>
                      <div className="selector">
                        <img
                          src={
                            tableIndex == 0
                              ? "../images/ic-non-select-left.png"
                              : "../images/ic-select-left.png"
                          }
                          onClick={() => {
                            selectTableIndex(0);
                          }}
                        ></img>{" "}
                        <p>{tableIndex == 0 ? "八字命盤" : "紫微命盤"}</p>
                        <img
                          src={
                            tableIndex == 1
                              ? "../images/ic-non-select-right.png"
                              : "../images/ic-select-right.png"
                          }
                          onClick={() => {
                            selectTableIndex(1);
                          }}
                        ></img>
                      </div>
                    </div>
                    <div className="chart-tag"></div>
                  </div>

                  <div className="chart">
                    {tableIndex == 0 && (
                      <BaziChartTable
                        baziPlate={chartData?.baziPlate}
                      ></BaziChartTable>
                    )}
                    {tableIndex == 1 && (
                      <ZiweiChartTable
                        ziweiData={ziweiData}
                        natalPlate={chartData?.ziweiPlate}
                        selectedDecadeIndex={selectedDecadeIndex}
                        selectedYear={selectedYear}
                        onSelectDecade={(idx) => fetchZiwei({ decadeIndex: idx })}
                        onSelectYear={(y) => fetchZiwei({ year: y })}
                      ></ZiweiChartTable>
                    )}
                  </div>
                </div>
              </div>
              <div className="space"></div>
              <div id="seven-aspects-section" className="right-block">
                <p className="tabs-title">七大面相分析</p>
                <AnaTabs
                  dataMap={TABS_MAP}
                  onChange={(tab, label) => {
                    setCurrentTab(label);
                  }}
                />
                <div
                  className={`analysis${isAnalyzing && !currentAspect ? " is-analyzing" : ""}`}
                >
                  {isAnalyzing && !currentAspect && (
                    <div className="analyzing-overlay">
                      <span className="loading-dots">分析中</span>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                      <span className="progress-text">{progressPercent}%</span>
                    </div>
                  )}
                  <div>
                    <p className="title">◆ 八字分析：</p>
                    <p className="des">{currentAspect?.simple?.bazi}</p>
                  </div>
                  {currentAspect?.simple?.ziwei && (
                    <div>
                      <p className="title">◆ 紫微分析：</p>
                      <p className="des">{currentAspect.simple.ziwei}</p>
                    </div>
                  )}
                  <div>
                    <p className="title">◆ 深度交叉解析：</p>
                    <div className="summarize">
                      <p
                        className="des"
                        dangerouslySetInnerHTML={{
                          __html: currentAspect?.detail || "",
                        }}
                      />
                    </div>
                  </div>
                  {currentTab !== "個性" && !chartData?.hasUnlockedAspects && (
                    <div className="mark">
                      <div
                        className="unlock"
                        onClick={handlePurchase}
                      >
                        <img src={`/images/ic-white-lock.png`}></img>
                        <span>NT$99</span>
                        <span>解鎖所有面向完整解析</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/*隱私政策與聯絡官方*/}
          <SiteFooter />

          <NavLink className="nav-link" to="/ask-teacher">
            <img className="question" src="../images/ic-question.png" />
          </NavLink>
        </div>
      )}

      {/* 手機板佈局 */}
      {isMobile && (
        <div className="mobile-main">
          {/* 個人資訊 */}
          <div className="personal-info">
            <div className="left-info">
              <p className="name">{chartData?.userName}</p>
              <div className="space"></div>
              <p style={birthCoord ? undefined : { visibility: "hidden" }}>
                地點 {birthCoord}
              </p>
            </div>
            <div className="right-info">
              <div className="inner">
                <p>性別 {chartData?.userGender == "1" ? "男" : "女"}</p>
                <p>西元 {chartData?.baziPlate?.solarBirth?.split(" ")[0]}</p>
                <p>
                  時間 {chartData?.baziPlate?.solarBirth?.split(" ")[1]} (
                  {chartData?.baziPlate?.solarBirth?.split(" ")[2]})
                </p>
              </div>
            </div>
          </div>

          {/* 命盤切換 */}
          <div className="chart-plate">
            <div className="chart-header">
              <div className="chart-select">
                <button disabled="false">
                  【命主】{chartData?.baziPlate?.dayMaster}
                  {GAN_ELEMENT[chartData?.baziPlate?.dayMaster] ?? ""}
                </button>
                <div className="selector">
                  <img
                    src={
                      tableIndex == 0
                        ? "../images/ic-non-select-left.png"
                        : "../images/ic-select-left.png"
                    }
                    onClick={() => selectTableIndex(0)}
                  ></img>
                  <p>{tableIndex == 0 ? "八字命盤" : "紫微命盤"}</p>
                  <img
                    src={
                      tableIndex == 1
                        ? "../images/ic-non-select-right.png"
                        : "../images/ic-select-right.png"
                    }
                    onClick={() => selectTableIndex(1)}
                  ></img>
                </div>
              </div>
            </div>
            <div className="chart">
              {tableIndex == 0 && (
                <BaziChartTable
                  baziPlate={chartData?.baziPlate}
                ></BaziChartTable>
              )}
              {tableIndex == 1 && (
                <ZiweiChartTable
                  ziweiData={ziweiData}
                  natalPlate={chartData?.ziweiPlate}
                  selectedDecadeIndex={selectedDecadeIndex}
                  selectedYear={selectedYear}
                  onSelectDecade={(idx) => fetchZiwei({ decadeIndex: idx })}
                  onSelectYear={(y) => fetchZiwei({ year: y })}
                ></ZiweiChartTable>
              )}
            </div>
          </div>

          {/* 七大面相分析 */}
          <div id="seven-aspects-section" className="right-block">
            <p className="tabs-title">七大面相分析</p>
            <AnaTabs
              dataMap={TABS_MAP}
              onChange={(tab, label) => {
                setCurrentTab(label);
              }}
            />
            <div
              className={`analysis${isAnalyzing && !currentAspect ? " is-analyzing" : ""}`}
            >
              {isAnalyzing && !currentAspect && (
                <div className="analyzing-overlay">
                  <span className="loading-dots">分析中</span>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <span className="progress-text">{progressPercent}%</span>
                </div>
              )}
              <div>
                <p className="title">◆ 八字分析：</p>
                <p className="des">{currentAspect?.simple?.bazi}</p>
              </div>
              {/* <div> */}
              {/* <p className="title">◆紫微分析：</p> */}
              {/* <p className="des">
                  命宮中天府星安穩，性格中有著穩定與沉著的特質，天同星也增添了親和力，能吸引他人靠近，然而又有些過於敏感。
                </p> */}
              {/* </div> */}
              <div>
                <p className="title">◆ 深度交叉解析：</p>
                <div className="summarize">
                  <p
                    className="des"
                    dangerouslySetInnerHTML={{
                      __html: currentAspect?.detail || "",
                    }}
                  />
                </div>
              </div>
              {currentTab !== "個性" && !chartData?.hasUnlockedAspects && (
                <div className="mark">
                  <div className="unlock" onClick={handlePurchase}>
                    <img src={`/images/ic-white-lock.png`}></img>
                    <span>NT$99</span>
                    <span>解鎖所有面向完整解析</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/*隱私政策與聯絡官方*/}
          <SiteFooter />

          {/* 手機版：滑到七大面向才顯示問問老師，避免擋命盤 */}
          {askVisible && (
            <NavLink className="nav-link" to="/ask-teacher">
              <img className="question" src="../images/ic-question.png" />
            </NavLink>
          )}
        </div>
      )}
    </div>
  );
};

export default PersonalChartPageComponent;
